import dayjs from "dayjs";
import Project, { initialJob, ProjectNote, ProjectState, ResourceOrder } from "../Project";
import { Fragment } from "react/jsx-runtime";
import { useMemo, useContext, useCallback, useState } from "react";
import { HCalendarContext } from "@/components/shared/HorizontalCalendar";
import HorizontalWeek from "./HorizontalWeek";
import ApiService from "@/api/ApiService";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { BookingOrderCombiner, ProcessedBooking, ProcessedOrder, RealBookingStruct, ResourceOrderStruct } from "./BookingOrderCombiner";
import { DragItem, DragType, useDragSubscriber } from "./CustomDragContext";
import useRights from "@/shared/api/useRights";
import { convertValueToMeridiem } from "@mui/x-date-pickers/internals/utils/time-utils";
import { usePersistantStore } from "@/stores/global";

const blankImage = new Image();
blankImage.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=";

const addRejectionsAndVacations = (
  combinedBookings: (ProcessedBooking | ProcessedOrder)[],
  rejections: { [id: number]: any },
  vacations: number[],
) => {
  const out = combinedBookings.map((cb) =>
    cb.isOrder && cb.id > 0 && cb.id in rejections
      ? {
          ...cb,
          combinationId: cb.combinationId + "_rejected_" + rejections[cb.id].comment,
          rejected: true,
          rejection: rejections[cb.id],
        }
      : !cb.isOrder && (cb.type === "HUMAN" || cb.type === "CREW_MEMBER") && vacations.includes(cb.id)
        ? {
            ...cb,
            combinationId: cb.combinationId + "_vacation",
            vacation: true,
          }
        : cb,
  );
  return out;
};

const getPrefix = (type) => {
  switch (type) {
    case "CREW":
      return "Kolonne ";
    case "HUMAN":
      return "";
    default:
      return "";
  }
};

interface Patch {
  [phaseUuid: string]: {
    plannedStart?: string;
    plannedEnd?: string;
    jobs: {
      [jobUuid: string]: string | null;
    };
  };
}

export interface PhasePlanning {
  weekStart: string;
  weekEnd: string;
  rowStart: number;
  phaseData: {
    uuid: string;
    plannedStart: string;
    plannedEnd: string;
    dates: { date: string; saveDate: string; bookingState: boolean; bookings: (ProcessedOrder | ProcessedBooking)[] }[];
  }[];
  notes: ProjectNote[];
}

const generatePatch = (phases, item, date, startDate, hasElevatedRights): Patch => {
  switch (item.type) {
    case "phase_start":
    case "phase_end":
      const phaseUuid = item.uuid;
      let { plannedStart: x1, plannedEnd: x2, jobs } = phases.find((p) => p.uuid === phaseUuid) ?? {};
      if (!x1 || !x2) return {};
      if (item.type === "phase_start") {
        x1 = date;
      } else {
        x2 = date;
      }
      const plannedStart = x1 < x2 ? x1 : x2;
      const plannedEnd = x1 > x2 ? x1 : x2;
      return {
        [phaseUuid]: {
          plannedStart,
          plannedEnd,
          jobs: Object.fromEntries(
            Object.values(jobs).flatMap((job) =>
              !!job.deleted ? [] : job.date < plannedStart || job.date > plannedEnd ? [[job.date, "DELETE"]] : [],
            ),
          ),
        },
      };
    case "job":
      const pUuid = item.phase;
      const { plannedStart: ps, plannedEnd: pe, jobs: jx } = phases.find((p) => p.uuid === pUuid) ?? {};
      if (!ps || !pe || !jx) return {};
      const fadd = dayjs(date, "YYYY-MM-DD").diff(dayjs(startDate, "YYYY-MM-DD"), "day");
      const lockOrder = true;
      let add = fadd;
      const newAddMap: { [date: string]: string } = {};
      if (!lockOrder && add !== 0) {
        const origin = dayjs(startDate, "YYYY-MM-DD");
        newAddMap[startDate] = origin.add(add, "days").format("YYYY-MM-DD");
      } else if (add !== 0) {
        const origin = dayjs(startDate, "YYYY-MM-DD");
        const dates = Object.values(jx)
          .filter((j) => !j.deleted)
          .map((x) => [x.date, hasElevatedRights || x.status === "PLANNING"])
          .sort(add > 0 ? (a, b) => a[0].localeCompare(b[0]) : (a, b) => b[0].localeCompare(a[0]));

        if (!hasElevatedRights) {
          let count = 0;
          let firstFixed = null;
          for (const [c, moveable] of dates) {
            if (c === startDate && !moveable) return {};
            if ((add > 0 && c <= startDate) || (add < 0 && c >= startDate)) continue;
            if (!moveable) {
              firstFixed = dayjs(c, "YYYY-MM-DD");
              break;
            }
            count++;
          }
          let lastPossible = null;
          if (!!firstFixed && count > 0) {
            lastPossible = firstFixed;
            while (true) {
              if ((add > 0 && lastPossible.day() === 1) || (add < 0 && lastPossible.day() === 5)) {
                lastPossible = lastPossible.subtract(add > 0 ? 3 : -3, "day");
              } else {
                lastPossible = lastPossible.subtract(add > 0 ? 1 : -1, "day");
              }
              count--;
              if (count <= 0) break;
            }
            lastPossible = lastPossible.subtract(add > 0 ? 1 : -1, "day");
          } else if (!!firstFixed) {
            lastPossible = firstFixed.subtract(add > 0 ? 1 : -1, "day");
          }
          if (!!lastPossible) {
            const xdiff = lastPossible.diff(origin, "days");
            if (add > 0) {
              add = Math.min(xdiff, add);
            } else {
              add = Math.max(xdiff, add);
            }
          }
        }

        if (add !== 0) {
          newAddMap[startDate] = origin.add(add, "days").format("YYYY-MM-DD");
          let lastDate = newAddMap[startDate];
          for (const [c, moveable] of dates) {
            if ((add > 0 && c <= startDate) || (add < 0 && c >= startDate)) continue;
            if ((add > 0 && lastDate < c) || (add < 0 && lastDate > c)) break;
            let newDate = dayjs(lastDate, "YYYY-MM-DD").add(add > 0 ? 1 : -1, "day");
            while (newDate.isoWeekday() > 5 && !dates.includes(newDate.format("YYYY-MM-DD"))) {
              newDate = newDate.add(add > 0 ? 1 : -1, "day");
            }
            newAddMap[c] = newDate.format("YYYY-MM-DD");
            lastDate = newAddMap[c];
          }
        }
      }

      const keys = Object.values(newAddMap).sort();
      const [lowest, highest] = keys.length > 0 ? [keys[0], keys[keys.length - 1]] : [ps, pe];

      return {
        [pUuid]: {
          plannedStart: lowest < ps ? lowest : ps,
          plannedEnd: highest > pe ? highest : pe,
          jobs: newAddMap,
        },
      };
  }
};

export default function HorizontalPlanningCalendar(props: {
  setDateChangeNecessary: any;
  project: ProjectState;
  setField: (changes: Partial<ProjectState>, createBreakpoint?: boolean) => void;
  currentSelection: any;
  lockOrder: boolean;
  setCurrentSelection: any;
}) {
  const view = useContext(HCalendarContext);
  const rights = useRights();
  const [patch, setPatch] = useState<Patch>({});
  const [notePatch, setNotePatch] = useState<Record<string, ProjectNote>>({});

  const settings = usePersistantStore((state) => state.getSettings());
  const includeCrews = settings.hcIncludeCrews;
  const includeCrewStaff = settings.hcIncludeCrewMembers;
  const includeHumans = settings.hcIncludeHumans;
  const includeResources = settings.hcIncludeResources;
  const includeOrders = true;
  const includeNotes = settings.hcIncludeNotes;

  const saveNote = useCallback((note: ProjectNote) => {
    props.setField({ notes: props.project.notes.map(item => item.clientId === note.clientId ? { ...note, changed: true } : item) }, true);
  }, [props.project.notes, props.setField]);

  const applyPatch = useCallback(
    function (patch: Patch) {
      props.setField(
        {
          phases: props.project.phases.map((phase) =>
            phase.uuid in patch
              ? {
                  ...phase,
                  changed: true,
                  plannedStart: patch[phase.uuid].plannedStart ?? phase.plannedStart,
                  plannedEnd: patch[phase.uuid].plannedEnd ?? phase.plannedEnd,
                  jobs: Object.fromEntries(
                    Object.entries(phase.jobs).map(([oldDate, job]) => {
                      if (!(oldDate in patch[phase.uuid].jobs)) return [oldDate, job];
                      const newDate = patch[phase.uuid].jobs[oldDate];
                      if (!newDate) return [oldDate, job];
                      if (newDate === "DELETE") {
                        return [oldDate, { ...job, deleted: true, changed: true }];
                      } else {
                        return [newDate, { ...job, date: newDate, changed: true }];
                      }
                    }),
                  ),
                }
              : phase,
          ),
        },
        true,
      );
      props.setDateChangeNecessary(true);
    },
    [props.project.phases],
  );

  const hasElevatedRights = "DISPOSITION" in rights;

  const onDrag = useCallback(
    function (item: DragItem, type: DragType, date: string, startDate: string) {
      if (item.type?.startsWith("note_")) {
        const note = item.note as ProjectNote;
        const moveByDays = item.type === "note_move" ? dayjs(date).diff(dayjs(startDate), "day") : 0;
        const changedStart = item.type === "note_start" ? date : dayjs(note.startDate).add(moveByDays, "day").format("YYYY-MM-DD");
        const changedEnd = item.type === "note_end" ? date : dayjs(note.endDate).add(moveByDays, "day").format("YYYY-MM-DD");
        const changed = {
          ...note,
          startDate: changedStart < changedEnd ? changedStart : changedEnd,
          endDate: changedStart > changedEnd ? changedStart : changedEnd,
        };
        if (type === "MOVING") {
          setNotePatch({ [note.clientId]: changed });
        } else {
          setNotePatch({});
          if (date !== startDate) saveNote(changed);
        }
        return;
      }
      const patch = date === startDate ? {} : generatePatch(props.project.phases, item, date, startDate, hasElevatedRights);
      if (type === "MOVING") {
        setPatch(patch);
      } else {
        setPatch({});
        applyPatch(patch);
      }
    },
    [props.project.phases, hasElevatedRights, applyPatch, saveNote],
  );
  useDragSubscriber(onDrag);

  const selectDate = (phaseUuid: string, clickDate?: string, allowCreation: boolean = true, referenceDate?: string) => {
    if (!!clickDate && allowCreation) {
      const toCreate = new Set([clickDate]);
      props.setField(
        {
          phases: props.project.phases.map((phase) =>
            phase.uuid !== phaseUuid
              ? phase
              : {
                  ...phase,
                  jobs: {
                    ...phase.jobs,
                    ...Object.fromEntries(
                      Array.from(toCreate).map((date) =>
                        date in phase.jobs && phase.jobs[date].deleted
                          ? [date, { ...phase.jobs[date], deleted: false, changed: true }]
                          : [date, initialJob(date)],
                      ),
                    ),
                  },
                },
          ),
        },
        true,
      );
    } else {
      const phase = props.project.phases.findIndex((p) => p.uuid === phaseUuid);
      props.setCurrentSelection({ phase, date: clickDate || null, referenceDate });
    }
  };

  const bookingQuery = useQuery<RealBookingStruct, AxiosResponse>({
    queryKey: [
      "getAllRealBookings",
      "" + props.project.id,
      includeCrewStaff ? "crews" : "nocrews",
      includeHumans ? "humans" : "nohumans",
      includeCrews ? "crews" : "nocrews",
      includeResources ? "resources" : "noresources",
    ],
    queryFn: ApiService.getAllRealBookings(props.project.id, {
      staff: includeCrewStaff,
      humans: includeHumans,
      crews: includeCrews,
      resources: includeResources,
    }),
    enabled: (props.project?.id ?? 0) > 0,
  });

  const jobStateQuery = useQuery<Record<string, boolean>, AxiosResponse>({
    queryKey: ["getJobStates", "" + props.project.id],
    queryFn: ApiService.getJobStates(props.project.id),
    enabled: (props.project?.id ?? 0) > 0,
  });

  const orderRejectionQuery = useQuery<Record<string, Record<string, Record<number, any>>>, AxiosResponse>({
    queryKey: ["getProjectRejections", "" + props.project.id],
    queryFn: ApiService.getProjectRejections(props.project.id),
    enabled: (props.project?.id ?? 0) > 0,
  });

  const vacationQuery = useQuery<Record<string, number[]>, AxiosResponse>({
    queryKey: ["getProjectVacations", "" + props.project.id],
    queryFn: ApiService.getProjectVacations(props.project.id),
    enabled: (props.project?.id ?? 0) > 0,
  });

  const tagNameQuery = useQuery<{ id: number; name: string }[], AxiosResponse>({
    queryKey: ["getTagNames", "" + props.project.region],
    queryFn: ApiService.getEntitiesWithFilter("resourceTag", { region: ["region", props.project.region] }, "names"),
  });

  const tagNames = useMemo(() => {
    if ((tagNameQuery.data ?? []).length === 0) return {};
    const t = {};
    for (const tag of tagNameQuery.data) {
      t[String(tag.id)] = tag.name;
    }
    return t;
  }, [tagNameQuery.data]);

  const resourceOrders: ResourceOrderStruct = useMemo(() => {
    const r: ResourceOrderStruct = {};
    if (!includeOrders) return r;
    for (let phase of props.project.phases) {
      if (phase.deleted) continue;
      const p = phase.uuid;
      r[p] = {};
      for (let [date, job] of Object.entries(phase.jobs)) {
        const j = job.uuid;
        r[p][j] = [];
        for (const ro of phase.resourceOrders) {
          if (
            !!ro.deleted ||
            (!!ro.startConstraint && date < ro.startConstraint) ||
            (!!ro.endConstraint && date > ro.endConstraint) ||
            ro.exceptions.includes(job.uuid) ||
            (ro.type === "CREW" && !includeCrews) ||
            (ro.type === "HUMAN" && !includeHumans) ||
            (ro.type === "RESOURCE" && !includeResources)
          )
            continue;
          r[p][j].push({
            ...ro,
            icon: !!ro.startConstraint || !!ro.endConstraint ? "constraint" : null,
            tagName: getPrefix(ro.type) + (tagNames[String(ro.tag ?? 0)] ?? "Unbekannte Kategorie"),
          });
        }
        for (const ro of job.resourceOrders) {
          if (
            !!ro.deleted ||
            (ro.type === "CREW" && !includeCrews) ||
            (ro.type === "HUMAN" && !includeHumans) ||
            (ro.type === "RESOURCE" && !includeResources)
          )
            continue;
          r[p][j].push({ ...ro, icon: "day", tagName: getPrefix(ro.type) + (tagNames[String(ro.tag ?? 0)] ?? "Unbekannte Kategorie") });
        }
      }
    }
    return r;
  }, [props.project.phases, tagNames, includeOrders, includeResources, includeCrews, includeHumans]);

  const combinedBookings = useMemo(() => {
    return BookingOrderCombiner.combine(bookingQuery.data ?? {}, resourceOrders);
  }, [resourceOrders, bookingQuery.data]);

  const weeks: PhasePlanning[] = useMemo(() => {
    const out: PhasePlanning[] = [];
    if (!view?.calStart || !view?.calEnd) return out;

    let weekStart = dayjs(view.calStart);
    const calEnd = dayjs(view.calEnd);
    let rowStart = 0;

    // Filter deleted phases once per memo
    const activePhases = props.project.phases
      .filter((p) => {
        if (p.deleted) return false;

        if (!p.plannedStart || !p.plannedEnd) return false;

        const start = dayjs(p.plannedStart);
        const end = dayjs(p.plannedEnd);

        if (!start.isValid() || !end.isValid()) return false;

        if (start.isAfter(end)) return false;

        return true;
      })
      .sort((a, b) =>
        (a.plannedStart ?? "9999-99-99") < (b.plannedStart ?? "9999-99-99")
          ? -1
          : (b.plannedStart ?? "9999-99-99") < (a.plannedStart ?? "9999-99-99")
            ? 1
            : 0,
      );

    while (weekStart.isSame(calEnd) || weekStart.isBefore(calEnd)) {
      const weekStartStr = weekStart.format("YYYY-MM-DD");
      const weekEndStr = weekStart.clone().add(7, "day").format("YYYY-MM-DD");

      const phaseData = activePhases.flatMap((phase) => {
        const { plannedStart: psPatch, plannedEnd: pePatch, jobs: jobPatch = {} } = patch[phase.uuid] ?? {};
        const plannedStart = psPatch ?? phase.plannedStart ?? "9999-99-99";
        const plannedEnd = pePatch ?? phase.plannedEnd ?? "0000-00-00";

        // Phase overlaps the week window?
        if (!(plannedStart < weekEndStr && plannedEnd >= weekStartStr)) {
          return [];
        }

        // Collect job dates in this week using flatMap (single pass)
        const dates = Object.values(phase.jobs ?? {})
          .flatMap((job) => {
            const date = jobPatch?.[job.date] ?? job.date;

            if (date !== "DELETE" && !job.deleted && date >= weekStartStr && date < weekEndStr) {
              return [
                {
                  date,
                  bookingState: !!(jobStateQuery.data ?? {})[job.uuid],
                  saveDate: job.date,
                  bookings: addRejectionsAndVacations(
                    combinedBookings?.[phase.uuid]?.[job.uuid] ?? [],
                    orderRejectionQuery?.data?.[phase.uuid]?.[date] ?? {},
                    !!vacationQuery.data && date in vacationQuery.data ? vacationQuery.data[date] : [],
                  ),
                },
              ];
            }

            return [];
          })
          .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

        return [{ uuid: phase.uuid, plannedEnd, plannedStart, dates }];
      });

      const notes = includeNotes ? props.project.notes
        .map(note => notePatch[note.clientId] ?? note)
        .filter(note => !note.deleted && note.startDate < weekEndStr && note.endDate >= weekStartStr) : [];
      if (phaseData.length > 0 || notes.length > 0) {
        out.push({ weekStart: weekStartStr, weekEnd: weekEndStr, phaseData, notes, rowStart });
      }

      weekStart = weekStart.add(7, "day");
      rowStart++;
    }

    return out;
  }, [view?.calStart, view?.calEnd, props.project.phases, props.project.notes, combinedBookings, jobStateQuery.data, patch, notePatch, orderRejectionQuery.data, vacationQuery.data, includeNotes]);

  return (
    <Fragment>
      {weeks.map((w) => (
        <HorizontalWeek
          phases={props.project.phases}
          key={w.weekStart}
          weekEnd={w.weekEnd}
          selectDate={selectDate}
          rowStart={w.rowStart * 100 + (view?.offset ?? 0) + 1}
          weekStart={w.weekStart}
          phaseData={w.phaseData}
          notes={w.notes}
          writeable={props.project.writeable}
          saveNote={saveNote}
        />
      ))}
    </Fragment>
  );
}
